import Image from 'next/image';

import { Box, Card, Flex, Grid, Heading, LinkBox, LinkOverlay, List, ListItem, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';

import { ChevronRightIcon } from '../icons';

export type CatalogSubcategoriesSectionProps = StaticCatalogSubcategoriesSectionProps;

export function CatalogSubcategoriesSection({ title, description, subcategories }: CatalogSubcategoriesSectionProps) {
  return (
    <Box as='section'>
      <ContentContainer contentMaxWidth='content-max-width-md'>
        <Card
          bgGradient='linear(78.15deg, #031643 0%, #00298C 50%, #6100B0 100%)'
          boxShadow='none'
          color='white'
          overflow='hidden'
          position='relative'
          borderRadius={{
            base: '0px',
            md: '12px',
          }}
          mx={{
            base: '-16px',
            md: 'unset',
          }}
          padding={{
            base: '40px 16px',
            md: 12,
          }}
        >
          <Box height='584px' position='absolute' right={0} top={0} width='160px'>
            <Image
              alt=''
              loading='lazy'
              quality={75}
              src='/images/experiments/ab_plp_redesign_2025/diagonal-lines.svg'
              style={{
                objectFit: 'contain',
                pointerEvents: 'none',
                transform: 'rotate(-90deg) scale(4) translate(20px, -15px)',
                objectPosition: 'right',
                mixBlendMode: 'overlay',
              }}
              fill
            />
          </Box>

          <Flex
            borderBottom='1px solid'
            borderColor='hsla(0, 0%, 100%, 0.2)'
            position='relative'
            gap={{
              base: 5,
              md: 6,
            }}
            pb={{
              base: 4,
              md: 8,
            }}
          >
            <Box
              flexShrink={0}
              height={{
                base: '52px',
                md: '64px',
              }}
              width={{
                base: '52px',
                md: '64px',
              }}
            >
              <Image
                alt='/'
                height={64}
                loading='lazy'
                src='/images/experiments/ab_plp_redesign_2025/skills-icon.svg'
                width={64}
              />
            </Box>
            <Box>
              <Heading
                as='h2'
                fontWeight='600'
                mb={2}
                fontSize={{
                  base: '1.375rem',
                  md: '2.25rem',
                }}
                lineHeight={{
                  base: '1.2',
                  md: '1.1',
                }}
                minHeight={{
                  base: '52px',
                  md: 'unset',
                }}
              >
                {title}
              </Heading>
              <Text
                marginLeft={{
                  base: '-72px',
                  md: 'unset',
                }}
              >
                {description}
              </Text>
            </Box>
          </Flex>

          <Grid
            as={List}
            gap={3}
            gridTemplateColumns={{
              base: 'repeat(auto-fill, minmax(min(100%, 173px), 1fr))',
              md: 'repeat(auto-fill, minmax(min(100%, 312px), 1fr))',
            }}
            mt={{
              base: 4,
              md: 8,
            }}
          >
            {subcategories.map((subcategory) => (
              <LinkBox
                key={subcategory._id}
                alignItems='center'
                as={ListItem}
                bgColor='hsla(0, 0%, 100%, 0.08)'
                border='1px solid'
                borderColor='hsla(0, 0%, 100%, 0.08)'
                borderRadius='4px'
                display='flex'
                gap={2}
                justifyContent='space-between'
                p={{
                  base: '12px',
                  md: '16px 18px',
                }}
              >
                <LinkOverlay
                  fontSize='0.875rem'
                  href={`/course-collection/${subcategory.slug}`}
                  lineHeight='1.4'
                  noOfLines={1}
                  wordBreak='break-all'
                >
                  {subcategory.title}
                </LinkOverlay>
                <ChevronRightIcon height='18px' width='18px' />
              </LinkBox>
            ))}
          </Grid>
        </Card>
      </ContentContainer>
    </Box>
  );
}

export async function transformStaticCatalogSubcategoriesSectionProps() {
  const SUBCATEGORY_SLUGS = [
    'product-development-and-strategy',
    'generative-ai-and-large-language-models',
    'ai-fundamentals',
    'machine-learning-and-deployment',
    'python-programming-for-all-levels',
    'web-development-and-web-design',
    'advanced-javascript-fundamentals-and-concepts',
    'mobile-design-and-development',
    'programming-and-computer-science-fundamentals',
    'natural-language-processing-and-llms',
    'digital-marketing-and-freelance',
    'data-engineering-and-data-automation',
  ];

  const { getAllSubcategoryContents } = await import('~/features/seo/controllers/get-all-subcategory-contents');
  const allSubcategoriesContent = await getAllSubcategoryContents({
    slug: {
      in: SUBCATEGORY_SLUGS,
    },
  });

  return {
    title: 'Skill collections for every tech path',
    description: 'Browse hand-picked course groups designed to build real skills in trending domains.',
    subcategories: allSubcategoriesContent
      .map((subcategory) => ({
        _id: subcategory._id,
        slug: subcategory.slug,
        title: subcategory.pageTitle,
      }))
      .sort((a, b) => {
        return SUBCATEGORY_SLUGS.indexOf(a.slug) - SUBCATEGORY_SLUGS.indexOf(b.slug);
      }),
  };
}

export type StaticCatalogSubcategoriesSectionProps = Awaited<
  ReturnType<typeof transformStaticCatalogSubcategoriesSectionProps>
>;
